import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import {
  TextField,
  Button,
  Paper,
  Chip,
  FormControlLabel,
  RadioGroup,
  Radio,
  LinearProgress,
  CircularProgress,
  Collapse,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import PlaylistAddIcon from "@material-ui/icons/PlaylistAdd";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ArrowRightAltIcon from "@material-ui/icons/ArrowRightAlt";
import LayersOutlinedIcon from "@material-ui/icons/LayersOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";

import HttpService from "../services/httpService";
import { setSnackInfo } from "../redux/actions/appAction";

const DEFAULT_TEMPLATE = "Snag30";
const DEFAULT_PREFIX = "Snag";
const MAX_BATCH = 200;
const PREFIX_PATTERN = /^[A-Za-z]{1,20}$/;

// Same palette as the rest of the ERP's newer pages, so this does not read as
// a different product.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const INK = "#3d2b21";
const GREEN_DARK = "#1f7a4d";
const GREEN_LIGHT = "#e8f6ee";

const BrandLinearProgress = withStyles(() => ({
  root: { height: 3, borderRadius: 999, backgroundColor: "rgba(193,83,31,0.10)" },
  bar: { borderRadius: 999, background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)` },
}))(LinearProgress);

// The server answers errors with res.status(400).json('some string'), but a
// 500 from anywhere else in the stack sends an object - and rendering an
// object as a React child throws. Everything is reduced to a string here.
const serverMessage = (error, fallback) => {
  const data = error && error.response ? error.response.data : null;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data.message === "string" && data.message.trim()) return data.message;
  if (data && typeof data.error === "string" && data.error.trim()) return data.error;
  if (error && typeof error.message === "string" && error.message.trim()) return error.message;
  return fallback;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const useStyles = makeStyles((theme) => ({
  // Sized to the viewport so the page itself never scrolls - the two panels
  // scroll inside instead. 144px is the app chrome above: antd Header 64 +
  // Content margin 16*2 + Content padding 24*2 (see components/sidebar.js).
  root: {
    height: "calc(100vh - 144px)",
    minHeight: 520,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    overflow: "auto",
  },
  // Below this the two columns stack, and a fixed height would trap the second
  // one off-screen.
  "@media (max-height: 640px)": {
    root: { height: "auto", minHeight: 0 },
  },

  // ---- header -------------------------------------------------------------
  header: {
    flexShrink: 0,
    borderRadius: 16,
    padding: "16px 22px",
    background: `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 62%, #ff9c66 100%)`,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  headerIcon: {
    width: 42,
    height: 42,
    borderRadius: 13,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    // Solid rather than a translucent white: on the light end of the gradient
    // a frosted panel loses almost all of its contrast.
    background: "rgba(0,0,0,0.18)",
  },
  headerTitle: { fontSize: 17, fontWeight: 800, letterSpacing: 0.2, lineHeight: 1.2 },
  headerHint: { fontSize: 12.5, marginTop: 3, color: "rgba(255,255,255,0.92)", lineHeight: 1.45 },
  headerBadge: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    background: "rgba(0,0,0,0.20)",
    borderRadius: 999,
    padding: "6px 12px",
  },

  // ---- two-column body ----------------------------------------------------
  body: {
    flex: 1,
    minHeight: 0,
    display: "grid",
    gridTemplateColumns: "minmax(380px, 460px) minmax(0, 1fr)",
    gap: 14,
  },
  "@media (max-width: 1023.95px)": {
    body: { gridTemplateColumns: "minmax(0, 1fr)" },
  },

  // ---- "something is happening" strip -------------------------------------
  // Collapsed rather than held open at a reserved height: an always-present
  // empty band above the first field reads as a layout mistake, and collapsing
  // means the card never jumps when the bar appears.
  activity: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 20px 0",
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: 0.2,
    color: ORANGE_DARK,
  },
  activityDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
    background: ORANGE_DARK,
    animation: "$pulse 1.1s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
    "50%": { opacity: 1, transform: "scale(1)" },
  },

  card: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    borderRadius: 16,
    border: "1px solid #ece8e5",
    boxShadow: "0 1px 2px rgba(61,43,33,0.04)",
    overflow: "hidden",
  },
  cardScroll: { flex: 1, minHeight: 180, overflow: "auto", padding: "4px 20px 20px" },

  // ---- numbered step ------------------------------------------------------
  step: { display: "flex", alignItems: "center", gap: 10, padding: "18px 0 12px" },
  stepNo: {
    width: 22,
    height: 22,
    borderRadius: 7,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    fontSize: 11.5,
    fontWeight: 800,
    color: "#fff",
    background: ORANGE_DARK,
  },
  stepTitle: { fontSize: 12.5, fontWeight: 800, color: ORANGE_DARK, textTransform: "uppercase", letterSpacing: 0.5 },
  stepRule: { flex: 1, height: 1, background: "#f0ece9" },

  field: { marginTop: 4 },
  fieldNote: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 7,
    fontSize: 11.5,
    color: "#756e68",
    minHeight: 18,
  },
  fieldNoteStrong: { fontWeight: 700, color: INK },

  optionCode: { fontSize: 13, fontWeight: 700, color: INK },
  optionClient: { fontSize: 11.5, color: "#8a8079", marginTop: 1 },

  // ---- mode switch --------------------------------------------------------
  // A segmented pair rather than loose radios: the two are mutually exclusive
  // and each one changes which fields appear, which bare radios do not signal.
  modes: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 2 },
  modeCard: {
    margin: 0,
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid #ece8e5",
    background: "#fbfaf9",
    cursor: "pointer",
    transition: "border-color 150ms ease, background-color 150ms ease",
    "&:hover": { borderColor: "#f3c9b2" },
  },
  modeCardOn: { borderColor: ORANGE_DARK, background: ORANGE_LIGHT },
  modeLabel: { fontSize: 12.5, fontWeight: 700, color: INK, lineHeight: 1.25 },
  modeHint: { fontSize: 11, color: "#8a8079", marginTop: 2, lineHeight: 1.35 },

  numbers: { display: "flex", gap: 12, marginTop: 14 },
  numberField: { flex: 1 },

  // ---- the live plan ------------------------------------------------------
  // The one thing the old page never showed: what you are about to get, before
  // you ask for it.
  plan: {
    marginTop: 16,
    borderRadius: 14,
    border: "1px solid #f3c9b2",
    background: ORANGE_LIGHT,
    padding: "13px 16px",
  },
  planLabel: {
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: "#a8450f",
  },
  planRange: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 6,
    fontSize: 17,
    fontWeight: 800,
    color: INK,
    flexWrap: "wrap",
  },
  planArrow: { color: ORANGE_DARK, fontSize: 22 },
  planCount: { fontSize: 12, color: "#5f574f", marginTop: 5 },

  problem: {
    marginTop: 16,
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    borderRadius: 14,
    border: "1px solid #f0d7d2",
    background: "#fdf3f1",
    padding: "12px 15px",
    fontSize: 12.5,
    color: "#8d3a2a",
    lineHeight: 1.45,
  },

  // ---- actions ------------------------------------------------------------
  actions: {
    flexShrink: 0,
    display: "flex",
    gap: 10,
    padding: "14px 20px",
    borderTop: "1px solid #f0ece9",
    background: "#fbfaf9",
  },
  previewBtn: {
    flex: 1,
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 11,
    borderColor: "#e3b79c",
    color: ORANGE_DARK,
    "&:hover": { borderColor: ORANGE_DARK, background: ORANGE_LIGHT },
  },
  createBtn: {
    flex: 1.35,
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 11,
    color: "#fff",
    background: ORANGE_DARK,
    boxShadow: "none",
    "&:hover": { background: "#a8450f", boxShadow: "none" },
    "&.Mui-disabled": { background: "#efe9e5", color: "#a89f98" },
  },
  createBtnSub: { display: "block", fontSize: 10.5, fontWeight: 600, opacity: 0.9, marginTop: 1 },

  // ---- right panel --------------------------------------------------------
  panelHead: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "16px 20px 12px",
  },
  panelTitle: { fontSize: 12.5, fontWeight: 800, color: ORANGE_DARK, textTransform: "uppercase", letterSpacing: 0.5 },
  panelCount: {
    marginLeft: "auto",
    fontSize: 11,
    fontWeight: 700,
    color: "#8a8079",
  },

  empty: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 10,
    padding: 28,
  },
  emptyTitle: { fontSize: 14, fontWeight: 700, color: "#5f574f" },
  emptyHint: { fontSize: 12.5, color: "#8a8079", maxWidth: 360, lineHeight: 1.55 },

  done: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "0 0 16px",
    borderRadius: 13,
    border: `1px solid ${GREEN_DARK}33`,
    background: GREEN_LIGHT,
    padding: "12px 15px",
    color: "#14543a",
    fontSize: 13,
    fontWeight: 600,
  },

  stats: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))", gap: 10, marginBottom: 16 },
  stat: { borderRadius: 13, border: "1px solid #ece8e5", background: "#fbfaf9", padding: "11px 14px" },
  statValue: { fontSize: 19, fontWeight: 800, color: INK, lineHeight: 1.1 },
  statLabel: { fontSize: 11, color: "#756e68", marginTop: 3 },

  group: { marginBottom: 16 },
  groupHead: { display: "flex", alignItems: "center", gap: 7, marginBottom: 9 },
  groupTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" },
  chips: { display: "flex", flexWrap: "wrap", gap: 7 },
  chipNew: {
    height: 24,
    fontSize: 11.5,
    fontWeight: 700,
    color: "#a8450f",
    background: ORANGE_LIGHT,
    border: "1px solid #f3c9b2",
  },
  chipSkip: {
    height: 24,
    fontSize: 11.5,
    fontWeight: 600,
    color: "#8a8079",
    background: "#f4f2f0",
    border: "1px solid #e7e2de",
  },
  skipNote: { fontSize: 11.5, color: "#8a8079", marginTop: 8, lineHeight: 1.5 },
}));

const SnagSeriesCreation = (props) => {
  const { setSnackInfo } = props;
  const classes = useStyles();

  const [templateCode, setTemplateCode] = useState(DEFAULT_TEMPLATE);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [prefix, setPrefix] = useState(DEFAULT_PREFIX);
  const [mode, setMode] = useState("count");
  const [count, setCount] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [series, setSeries] = useState(null);          // { prefix, last, next }
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [done, setDone] = useState(null);              // the last create that went through

  // Both lookups follow the prefix as it is typed. A monotonic id rather than
  // a cancelled flag: two requests can be in flight and the slower one must
  // not overwrite the newer answer.
  const templateReq = useRef(0);
  const seriesReq = useRef(0);

  const loadTemplates = useCallback(async (search) => {
    const id = ++templateReq.current;
    setTemplatesLoading(true);
    try {
      const result = await HttpService.getSnagSeriesTemplates(search);
      if (id !== templateReq.current) return;
      const rows = Array.isArray(result.data) ? result.data : [];
      setTemplateOptions(
        rows
          .filter((project) => project && project.code)
          .sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }))
      );
    } catch (error) {
      if (id === templateReq.current) setTemplateOptions([]);
    }
    if (id === templateReq.current) setTemplatesLoading(false);
  }, []);

  // Debounced: the list followed every keystroke before, so typing "Snag" was
  // four requests and the first one could land last.
  useEffect(() => {
    const timer = setTimeout(() => loadTemplates(prefix || DEFAULT_PREFIX), 300);
    return () => clearTimeout(timer);
  }, [loadTemplates, prefix]);

  // `done` is in the deps so the last existing number is re-read after a
  // create - the next batch has to continue from the ones just made.
  useEffect(() => {
    if (!PREFIX_PATTERN.test(prefix)) {
      setSeries(null);
      return undefined;
    }
    const id = ++seriesReq.current;
    setSeriesLoading(true);
    const timer = setTimeout(() => {
      HttpService.getSnagSeriesNextNumber(prefix)
        .then((result) => {
          if (id !== seriesReq.current) return;
          setSeries(result.data);
          setSeriesLoading(false);
        })
        .catch(() => {
          if (id !== seriesReq.current) return;
          setSeries(null);
          setSeriesLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [prefix, done]);

  // A preview describes the inputs it was made with and nothing else.
  useEffect(() => setPreview(null), [templateCode, prefix, mode, count, start, end]);

  // ---- validation ---------------------------------------------------------
  // Returns the reason rather than a bare boolean, so a disabled Preview
  // button can say why instead of just sitting there greyed out.
  const check = (() => {
    if (!PREFIX_PATTERN.test(prefix)) {
      return { ok: false, field: "prefix", message: "The code prefix can only be letters — no digits, spaces or symbols." };
    }
    if (!String(templateCode).trim()) {
      return { ok: false, field: "template", message: "Choose the project these copies are made from." };
    }
    const whole = (value) => /^\d+$/.test(String(value).trim()) && Number(value) >= 1;

    if (mode === "count") {
      if (!String(count).trim()) return { ok: false, field: "count", message: "Enter how many projects to create." };
      if (!whole(count)) return { ok: false, field: "count", message: "That has to be a whole number of 1 or more." };
      if (Number(count) > MAX_BATCH) return { ok: false, field: "count", message: `${MAX_BATCH} is the most that can be created in one go.` };
      if (!series) return { ok: false, field: "count", message: "Still reading the last existing number for this prefix." };
      return { ok: true };
    }
    if (!String(start).trim() || !String(end).trim()) {
      return { ok: false, field: "range", message: "Enter both the first and the last number." };
    }
    if (!whole(start) || !whole(end)) {
      return { ok: false, field: "range", message: "Both ends have to be whole numbers of 1 or more." };
    }
    if (Number(start) > Number(end)) {
      return { ok: false, field: "range", message: "The first number cannot be higher than the last." };
    }
    const span = Number(end) - Number(start) + 1;
    if (span > MAX_BATCH) {
      return { ok: false, field: "range", message: `That is ${span} projects — ${MAX_BATCH} is the most in one go.` };
    }
    return { ok: true };
  })();

  // What the inputs add up to, worked out here instead of waiting for the
  // server. In count mode it continues from the last existing number, exactly
  // as routes/snagSeries.js does.
  const plan = (() => {
    if (!check.ok) return null;
    if (mode === "count") {
      const from = series.last + 1;
      return { from, to: from + Number(count) - 1, total: Number(count) };
    }
    return { from: Number(start), to: Number(end), total: Number(end) - Number(start) + 1 };
  })();

  const buildPayload = (dryRun) => ({
    templateCode,
    prefix,
    dryRun,
    ...(mode === "count" ? { count: Number(count) } : { start: Number(start), end: Number(end) }),
  });

  const runPreview = async () => {
    setDone(null);
    setPreviewing(true);
    try {
      const result = await HttpService.createSnagSeries(buildPayload(true));
      setPreview(result.data);
      if (result.data.createdCount === 0) {
        setSnackInfo("Every project in that range already exists — nothing left to create.", "warning");
      }
    } catch (error) {
      setSnackInfo(serverMessage(error, "Could not work out what would be created"), "error");
    }
    setPreviewing(false);
  };

  const runCreate = async () => {
    setDone(null);
    setCreating(true);
    try {
      const result = await HttpService.createSnagSeries(buildPayload(false));
      setSnackInfo(`${plural(result.data.createdCount, "Snag project")} created`, "success");
      setCount("");
      setStart("");
      setEnd("");
      setPreview(null);
      // Set last: clearing the inputs above runs the reset effect, and `done`
      // is deliberately not cleared by it.
      setDone(result.data);
    } catch (error) {
      setSnackInfo(serverMessage(error, "Could not create the Snag projects"), "error");
    }
    setCreating(false);
  };

  const busy = previewing || creating;

  // What the progress bar is actually waiting on, in the order that matters:
  // a write outranks a read, and the two background lookups are the quietest.
  // A bar with no caption only says "wait"; this says what for.
  const activity = creating
    ? `Creating ${plural(preview ? preview.createdCount : 0, "project")} and copying their products…`
    : previewing
      ? "Checking which codes are free…"
      : seriesLoading
        ? `Reading the highest ${PREFIX_PATTERN.test(prefix) ? prefix : ""} number in use…`
        : templatesLoading
          ? "Fetching template projects…"
          : "";

  const shown = done || preview;
  const createdCodes = shown ? (shown.created || []).map((project) => project.code) : [];
  const skippedCodes = shown ? shown.skipped || [] : [];

  // Keeps the current value selectable even when the search has not returned
  // it - otherwise picking "Snag30" and then typing a different prefix would
  // blank the field.
  const options = templateOptions.some((o) => o.code === templateCode)
    ? templateOptions
    : [{ code: templateCode, clientName: "" }, ...templateOptions];
  const selected = options.find((o) => o.code === templateCode) || null;

  const prefixBad = Boolean(prefix) && !PREFIX_PATTERN.test(prefix);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerIcon}>
          <LayersOutlinedIcon style={{ fontSize: 23, color: "#fff" }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className={classes.headerTitle}>Create Snag Series</div>
          <div className={classes.headerHint}>
            Each project is a copy of the template and all of its products. BOMs for damaged or
            additional material are uploaded against them afterwards, from the BOM page.
          </div>
        </div>
        <div className={classes.headerBadge}>
          <LockOutlinedIcon style={{ fontSize: 14 }} />
          Tech &amp; Admin only
        </div>
      </div>

      <div className={classes.body}>
        {/* ------------------------------- setup ------------------------------ */}
        <Paper elevation={0} className={classes.card}>
          <div style={{ flexShrink: 0 }}>
            {/* The bar is driven by `activity`, not by `busy` - the two
                background lookups move it too, so a slow template fetch no
                longer looks like a frozen page. */}
            {activity ? <BrandLinearProgress /> : <div style={{ height: 3 }} />}
            <Collapse in={Boolean(activity)} timeout={180} unmountOnExit>
              <div className={classes.activity}>
                <span className={classes.activityDot} />
                {activity}
              </div>
            </Collapse>
          </div>

          <div className={classes.cardScroll}>
            <div className={classes.step}>
              <span className={classes.stepNo}>1</span>
              <span className={classes.stepTitle}>Copy from</span>
              <span className={classes.stepRule} />
            </div>

            <Autocomplete
              className={classes.field}
              disableClearable
              size="small"
              options={options}
              value={selected}
              loading={templatesLoading}
              onChange={(event, value) => value && setTemplateCode(value.code)}
              getOptionLabel={(option) => (option && option.code) || ""}
              getOptionSelected={(option, value) => option.code === value.code}
              noOptionsText={`No project code contains “${prefix || DEFAULT_PREFIX}”`}
              renderOption={(option) => (
                <div style={{ minWidth: 0 }}>
                  <div className={classes.optionCode}>{option.code}</div>
                  {option.clientName ? <div className={classes.optionClient}>{option.clientName}</div> : null}
                </div>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template project"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {templatesLoading ? <CircularProgress size={15} thickness={5} style={{ color: ORANGE_DARK }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <div className={classes.fieldNote}>
              <FolderOpenIcon style={{ fontSize: 14, color: "#a89f98" }} />
              {shown && shown.template
                ? <span>Copies <span className={classes.fieldNoteStrong}>{plural(shown.template.products, "product")}</span> into every new project</span>
                : <span>{templateOptions.length ? `${plural(templateOptions.length, "project")} match this prefix` : "Its products are copied into every new project"}</span>}
            </div>

            <div className={classes.step}>
              <span className={classes.stepNo}>2</span>
              <span className={classes.stepTitle}>Numbering</span>
              <span className={classes.stepRule} />
            </div>

            <TextField
              className={classes.field}
              fullWidth
              size="small"
              variant="outlined"
              label="Code prefix"
              value={prefix}
              error={prefixBad}
              onChange={(event) => setPrefix(event.target.value.trim())}
            />
            <div className={classes.fieldNote}>
              {prefixBad ? (
                <>
                  <ErrorOutlineIcon style={{ fontSize: 14, color: "#c0392b" }} />
                  <span style={{ color: "#c0392b" }}>Letters only — the numbers are added for you.</span>
                </>
              ) : (
                <>
                  <FormatListNumberedIcon style={{ fontSize: 14, color: "#a89f98" }} />
                  {seriesLoading && <span>Checking which numbers are taken…</span>}
                  {!seriesLoading && series && series.last > 0 && (
                    <span>
                      Highest so far <span className={classes.fieldNoteStrong}>{prefix}{series.last}</span> · next free{" "}
                      <span className={classes.fieldNoteStrong}>{prefix}{series.next}</span>
                    </span>
                  )}
                  {!seriesLoading && series && series.last === 0 && <span>Nothing uses this prefix yet — it would start at {prefix}1</span>}
                  {!seriesLoading && !series && <span>&nbsp;</span>}
                </>
              )}
            </div>

            <RadioGroup className={classes.modes} value={mode} onChange={(event) => setMode(event.target.value)}>
              <FormControlLabel
                value="count"
                className={`${classes.modeCard} ${mode === "count" ? classes.modeCardOn : ""}`}
                control={<Radio color="primary" size="small" style={{ padding: 4, marginRight: 4 }} />}
                label={
                  <span>
                    <span className={classes.modeLabel}>Continue the series</span>
                    <span className={classes.modeHint} style={{ display: "block" }}>Carries on after the highest number</span>
                  </span>
                }
              />
              <FormControlLabel
                value="range"
                className={`${classes.modeCard} ${mode === "range" ? classes.modeCardOn : ""}`}
                control={<Radio color="primary" size="small" style={{ padding: 4, marginRight: 4 }} />}
                label={
                  <span>
                    <span className={classes.modeLabel}>Exact range</span>
                    <span className={classes.modeHint} style={{ display: "block" }}>Fill a gap, or redo a set</span>
                  </span>
                }
              />
            </RadioGroup>

            <div className={classes.numbers}>
              {mode === "count" ? (
                <TextField
                  className={classes.numberField}
                  size="small"
                  variant="outlined"
                  label="How many"
                  placeholder={`1 – ${MAX_BATCH}`}
                  value={count}
                  error={check.field === "count" && Boolean(String(count).trim())}
                  // Digits only, so "1e5", "-4" and "2.5" never reach a field
                  // that is really asking for a whole number.
                  onChange={(event) => setCount(event.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                />
              ) : (
                <>
                  <TextField
                    className={classes.numberField}
                    size="small"
                    variant="outlined"
                    label="From number"
                    value={start}
                    error={check.field === "range" && Boolean(String(start).trim())}
                    onChange={(event) => setStart(event.target.value.replace(/[^\d]/g, "").slice(0, 7))}
                  />
                  <TextField
                    className={classes.numberField}
                    size="small"
                    variant="outlined"
                    label="To number"
                    value={end}
                    error={check.field === "range" && Boolean(String(end).trim())}
                    onChange={(event) => setEnd(event.target.value.replace(/[^\d]/g, "").slice(0, 7))}
                  />
                </>
              )}
            </div>

            {plan ? (
              <div className={classes.plan}>
                <div className={classes.planLabel}>You are about to create</div>
                <div className={classes.planRange}>
                  <span>{prefix}{plan.from}</span>
                  <ArrowRightAltIcon className={classes.planArrow} />
                  <span>{prefix}{plan.to}</span>
                </div>
                <div className={classes.planCount}>
                  {plural(plan.total, "project")}
                  {mode === "range" ? " · any that already exist are skipped" : ""}
                </div>
              </div>
            ) : (
              <div className={classes.problem}>
                <ErrorOutlineIcon style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }} />
                <span>{check.message}</span>
              </div>
            )}
          </div>

          <div className={classes.actions}>
            <Button
              variant="outlined"
              className={classes.previewBtn}
              disabled={!check.ok || busy}
              onClick={runPreview}
              startIcon={previewing
                ? <CircularProgress size={15} thickness={5} style={{ color: ORANGE_DARK }} />
                : <VisibilityOutlinedIcon style={{ fontSize: 18 }} />}
            >
              {previewing ? "Checking…" : "Preview"}
            </Button>
            <Button
              variant="contained"
              className={classes.createBtn}
              disabled={!preview || preview.createdCount === 0 || busy}
              onClick={runCreate}
              startIcon={creating
                ? <CircularProgress size={15} thickness={5} style={{ color: "#fff" }} />
                : <PlaylistAddIcon style={{ fontSize: 19 }} />}
            >
              <span>
                {creating ? "Creating…" : preview ? `Create ${plural(preview.createdCount, "project")}` : "Create"}
                {!creating && preview && preview.createdCount > 0 && (
                  <span className={classes.createBtnSub}>
                    {preview.created[0].code} – {preview.created[preview.created.length - 1].code}
                  </span>
                )}
              </span>
            </Button>
          </div>
        </Paper>

        {/* ------------------------------ outcome ----------------------------- */}
        <Paper elevation={0} className={classes.card}>
          {/* Only the two actions move this one - a template lookup has
              nothing to do with the result shown here. */}
          <div style={{ flexShrink: 0 }}>
            {busy ? <BrandLinearProgress /> : <div style={{ height: 3 }} />}
          </div>

          <div className={classes.panelHead}>
            {done
              ? <CheckCircleIcon style={{ fontSize: 17, color: GREEN_DARK }} />
              : <VisibilityOutlinedIcon style={{ fontSize: 17, color: ORANGE_DARK }} />}
            <span className={classes.panelTitle} style={done ? { color: GREEN_DARK } : undefined}>
              {done ? "Created" : "Preview"}
            </span>
            {shown && (
              <span className={classes.panelCount}>
                {plural(createdCodes.length, "project")}
                {skippedCodes.length ? ` · ${skippedCodes.length} skipped` : ""}
              </span>
            )}
          </div>

          {busy ? (
            // Said plainly, because a create of 200 projects also copies every
            // one of the template's products and is not instant.
            <div className={classes.empty}>
              <CircularProgress size={34} thickness={4} style={{ color: ORANGE_DARK }} />
              <div className={classes.emptyTitle}>
                {creating ? "Creating the projects…" : "Checking which codes are free…"}
              </div>
              <div className={classes.emptyHint}>
                {creating
                  ? `Copying ${plural(preview ? preview.template.products : 0, "product")} into each of ${plural(preview ? preview.createdCount : 0, "project")}. Please stay on this page.`
                  : "Nothing is written while this runs — it only reports what would be created."}
              </div>
            </div>
          ) : !shown ? (
            <div className={classes.empty}>
              <VisibilityOutlinedIcon style={{ fontSize: 46, color: "#dcd6d1" }} />
              <div className={classes.emptyTitle}>Nothing is written until you press Create</div>
              <div className={classes.emptyHint}>
                Press <b>Preview</b> to see the exact project codes, how many already exist,
                and how many products each copy will carry.
              </div>
            </div>
          ) : (
            <div className={classes.cardScroll} style={{ paddingTop: 0 }}>
              {done && (
                <div className={classes.done}>
                  <CheckCircleIcon style={{ fontSize: 19 }} />
                  <span>
                    {plural(done.createdCount, "Snag project")} created, each with{" "}
                    {plural(done.template.products, "product")} copied from {done.template.code}.
                  </span>
                </div>
              )}

              <div className={classes.stats}>
                <div className={classes.stat}>
                  <div className={classes.statValue}>{shown.template.code}</div>
                  <div className={classes.statLabel}>Template · {plural(shown.template.products, "product")}</div>
                </div>
                <div className={classes.stat}>
                  <div className={classes.statValue} style={{ color: done ? GREEN_DARK : ORANGE_DARK }}>
                    {shown.createdCount}
                  </div>
                  <div className={classes.statLabel}>{done ? "Created" : "Will be created"}</div>
                </div>
                <div className={classes.stat}>
                  <div className={classes.statValue} style={{ color: shown.skippedCount ? "#8a8079" : INK }}>
                    {shown.skippedCount}
                  </div>
                  <div className={classes.statLabel}>Already existed</div>
                </div>
                <div className={classes.stat}>
                  <div className={classes.statValue}>{shown.createdCount * shown.template.products}</div>
                  <div className={classes.statLabel}>Products in total</div>
                </div>
              </div>

              {createdCodes.length > 0 && (
                <div className={classes.group}>
                  <div className={classes.groupHead}>
                    <CheckCircleIcon style={{ fontSize: 15, color: done ? GREEN_DARK : ORANGE_DARK }} />
                    <span className={classes.groupTitle} style={{ color: done ? GREEN_DARK : ORANGE_DARK }}>
                      {done ? "Created" : "New"} · {createdCodes.length}
                    </span>
                  </div>
                  <div className={classes.chips}>
                    {createdCodes.map((code) => (
                      <Chip key={code} size="small" label={code} className={classes.chipNew} />
                    ))}
                  </div>
                </div>
              )}

              {skippedCodes.length > 0 && (
                <div className={classes.group}>
                  <div className={classes.groupHead}>
                    <RemoveCircleOutlineIcon style={{ fontSize: 15, color: "#8a8079" }} />
                    <span className={classes.groupTitle} style={{ color: "#8a8079" }}>
                      Skipped · {skippedCodes.length}
                    </span>
                  </div>
                  <div className={classes.chips}>
                    {skippedCodes.map((code) => (
                      <Chip key={code} size="small" label={code} className={classes.chipSkip} />
                    ))}
                  </div>
                  <div className={classes.skipNote}>
                    <InfoOutlinedIcon style={{ fontSize: 13, verticalAlign: -2, marginRight: 4 }} />
                    These codes are already in use. They are left exactly as they are — nothing is
                    overwritten, and their existing products and BOMs are untouched.
                  </div>
                </div>
              )}
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
};

export default connect(null, { setSnackInfo })(SnagSeriesCreation);